import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  GetInvoiceCharges,
  DeleteInvoiceCharge,
} from "../../../services/BillingMaintenanceService"; 
import CommonLoader from "../../common/CommonLoader";
import ViewInvoiceCharges from "./ViewInvoiceCharges";
import DeleteConfirmation from "../../common/DeleteConfirmation"; 
import { NotificationManager } from "react-notifications";
import AddEditInvoiceChargeDialog from "./AddEditInvoiceChargeDialog"; 

const InvoiceChargesTable = () => {
  const columns = ["Item Description", "Price", "GL Code", "Actions"];
  const columnHeaders = ["itemDescription", "price", "glCode"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [invoiceChargesData, setInvoiceChargesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoiceCharge, setSelectedInvoiceCharge] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false); 
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false); 
  const [editInvoiceCharge, setEditInvoiceCharge] = useState(null); 

  useEffect(() => {
    const fetchInvoiceCharges = async () => {
      try {
        setLoading(true);
        const response = await GetInvoiceCharges();
        setInvoiceChargesData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching invoice charges:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceCharges();
  }, []);

  useEffect(() => {
    const filteredResults = invoiceChargesData.filter((invoiceCharge) =>
      columnHeaders.some((header) =>
        String(invoiceCharge[header])
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, invoiceChargesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (invoiceCharge) => {
    setEditInvoiceCharge(invoiceCharge);
    setIsDialogOpen(true);
  };

  const handleAddClick = () => {
    setEditInvoiceCharge(null);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditInvoiceCharge(null);
  };

  const handleDeleteClick = (invoiceCharge) => {
    setSelectedItemToDelete(invoiceCharge);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteInvoiceCharge(selectedItemToDelete.glCode);
      if (response.results === true) {
        setIsDeleteConfirmationOpen(false);
        const updatedInvoiceCharges = invoiceChargesData.filter(
          (item) => item.glCode !== selectedItemToDelete.glCode
        );
        setInvoiceChargesData(updatedInvoiceCharges);
        setFilteredData(updatedInvoiceCharges);
        NotificationManager.success("Invoice charge deleted successfully");
      } else {
        setIsDeleteConfirmationOpen(false);
        NotificationManager.error("Error deleting invoice charge");
      }
    } catch (error) {
      NotificationManager.error("Error deleting invoice charge");
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleViewClick = (invoiceCharge) => {
    setSelectedInvoiceCharge(invoiceCharge);
  };

  const handleBackClick = () => {
    setSelectedInvoiceCharge(null);
    setSearchTerm("");
  };

  const renderActions = (invoiceCharge) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(invoiceCharge)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(invoiceCharge)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(invoiceCharge)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
    <span className="title">Invoice Charges </span>
    <hr />
      {selectedInvoiceCharge ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewInvoiceCharges invoiceCharge={selectedInvoiceCharge} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoiceCharge, index) => (
                      <TableRow
                        key={invoiceCharge.itemDescription}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>
                            {invoiceCharge[column]}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(invoiceCharge)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <AddEditInvoiceChargeDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            editInvoiceCharge={editInvoiceCharge}
          />
        </>
      )}

      {/* Render the delete confirmation popup */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete the ${selectedItemToDelete?.itemDescription}?`}
        color="red"
      />
    </>
  );
};

export default InvoiceChargesTable;
